/**
 * InfoScreen — Recap sicurezza, consigli, cosa rende Nullum diversa, GDPR.
 *
 * Pensata come "manuale dell'utente cosciente":
 *  1. Le tue impostazioni di sicurezza ATTUALI (introspettive)
 *  2. Come aumentare ancora la sicurezza (consigli concreti)
 *  3. Perché Nullum è tecnicamente diversa dalle altre piattaforme
 *  4. Privacy & GDPR (titolare: SKEMATIKA SRL)
 *  5. Guida rapida per scenario
 *
 * Sezioni collassabili per non spaventare l'utente con un wall of text.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame, Header, Footer, Btn, Box } from '../components/Frame';
import { useStore } from '../lib/store';
import { getVersionLabel } from '../lib/appVersion';
import { hasLocalBackup, loadLastSigil } from '../lib/pinCrypto';
import { areNotificationsEnabled } from '../lib/notifications';
import { isSelfReadable } from '../lib/messaging';
import { isNukeEnabled, setNukeEnabled } from '../lib/nuke';

function Section({ id, title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-ink-dim/50 rounded-xl mb-3 overflow-hidden bg-ink/[0.015]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex justify-between items-center gap-3 px-4 py-3 text-left text-[16px] text-ink text-glow hover:bg-ink/[0.06] transition-colors"
        aria-expanded={open}
      >
        <span>{title}</span>
        <span className={`text-ink-dim text-[15px] shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>▸</span>
      </button>
      {open && (
        <div className="px-4 pb-4 text-[14px] text-ink-dim space-y-2 border-t border-ink-dim/25 pt-3">
          {children}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, ok }) {
  // ok: true = verde, false = ambra (consiglio di sistemare), null = neutro
  const color = ok === true ? 'text-ink' : ok === false ? 'text-amber' : 'text-ink-dim';
  return (
    <div className="flex justify-between gap-3">
      <span className="text-ink-dim">{label}</span>
      <span className={`${color} text-right`}>{value}</span>
    </div>
  );
}

export default function InfoScreen() {
  const navigate = useNavigate();
  const { sigil } = useStore();
  const accountOnDevice = (() => { try { return hasLocalBackup() || !!loadLastSigil(); } catch { return false; } })();
  const notifOn = (() => { try { return areNotificationsEnabled(); } catch { return false; } })();
  const selfRead = (() => { try { return isSelfReadable(); } catch { return true; } })();
  const version = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev';
  const [verLabel, setVerLabel] = useState(`v${version}`);
  useEffect(() => { getVersionLabel().then(setVerLabel).catch(() => {}); }, []);
  const [nuke, setNuke] = useState(() => { try { return isNukeEnabled(); } catch { return false; } });

  function toggleNuke() {
    if (!nuke) {
      if (!confirm(
        'ATTIVARE NUKE 5-PIN?\n\n'
        + 'Se attivo, dopo 5 PIN ERRATI consecutivi su questo dispositivo Nullum '
        + 'CANCELLA TUTTO: chiavi, sigil, backup. L\'app torna come "mai installata".\n\n'
        + 'Serve contro la coercizione: chi ti forza il telefono e prova PIN a caso '
        + 'distrugge l\'accesso. TU recuperi con le 24 parole.\n\n'
        + 'ATTENZIONE: se sbagli tu il PIN 5 volte, perdi l\'accesso da questo device '
        + '(recuperabile solo con le 24 parole). Tienile al sicuro PRIMA di attivare.'
      )) return;
    }
    const v = !nuke;
    setNukeEnabled(v);
    setNuke(v);
  }

  return (
    <Frame statusLeft="INFO :: SICUREZZA" statusRight="5G">
      <Header title="// Sicurezza & info" crumb="◀ indietro" onCrumbClick={() => navigate('/inbox')} />

      <p className="text-[14px] text-ink-dim mb-3">
        Come stai usando Nullum, cosa puoi attivare per proteggerti di più
        e come trattiamo i tuoi dati.
      </p>

      {/* ============================================================ */}
      <Section id="state" title="🛡 Le tue impostazioni adesso" defaultOpen>
        <Row label="Pseudonimo (sigil)" value={`@${sigil || '???'}`} ok={null} />
        <Row label="Account su questo device" value={accountOnDevice ? 'sì' : 'no'} ok={accountOnDevice} />
        <Row label="Backup remoto cifrato" value="solo sul server" ok={true} />
        <Row label="Tentativi PIN" value="10/h, 30/24h (server)" ok={true} />
        <Row label="24 parole di recupero" value="custodia tua" ok={null} />
        <Row label="Notifiche push" value={notifOn ? 'attive' : 'spente'} ok={null} />
        <Row label="Rileggi i miei messaggi" value={selfRead ? 'ON' : 'OFF (deniability)'} ok={null} />
        <Row label="Codice sicurezza per chat" value="disponibile" ok={true} />
        <Row label="Aggiornamenti chat" value="in tempo reale" ok={true} />
        <Row label="Modalità accettazione" value="su richiesta" ok={null} />
        <Row label="Versione app" value={verLabel} ok={null} />
        <p className="text-[12px] text-ink-dim pt-2 border-t border-ink-dim/40">
          <span className="text-amber">⚠</span> &nbsp;
          Da un nuovo device rientri con <b>@sigil + PIN</b>: il server ti
          rimanda il tuo backup, un pacchetto cifrato che solo il tuo PIN
          può aprire. Le 24 parole ti salvano se dimentichi il PIN: custodiscile.
        </p>
      </Section>

      {/* ============================================================ */}
      <Section id="harden" title="🔧 Vuoi più sicurezza? Ecco cosa puoi fare">
        <p className="text-ink">A. <span className="text-ink-dim">Per ogni nuovo contatto</span></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Apri la chat → "🔒 codice di sicurezza" → leggetelo a voce.
            Se combacia, nessuno vi sta intercettando. Se un giorno cambia,
            vedi un avviso rosso.</li>
          <li>Di persona: crea un invito da "1 minuto", mostra il QR code,
            l'altro lo inquadra. Il link muore subito.</li>
          <li>Vuoi essere sicuro che sia lui? Manda un audio o video live
            (🎙 / 🎥 dal menu allegati): lo vedi e lo senti in diretta, una volta sola.</li>
        </ul>

        <p className="text-ink pt-2">B. <span className="text-ink-dim">Per messaggi sensibili</span></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Imposta un tempo "10s" o "zero": il messaggio sparisce dopo
            la lettura.</li>
          <li>Disattiva "Rileggi i miei messaggi" da menu → ⚙ Impostazioni:
            nemmeno tu potrai più rileggerli, quindi non li trova nessuno.</li>
          <li>Allegati che si vedono una volta sola: foto, video, audio, file
            fino a 24 MB cifrati. Dopo l'apertura spariscono dal server.</li>
          <li>Prima di inviare un'immagine ne vedi un'<b>anteprima grande</b>:
            controlli sempre cosa stai mandando.</li>
          <li>Sbagliato a inviare? Tocca "✕ annulla" finché l'altro non l'ha
            aperto: sparisce del tutto.</li>
        </ul>

        <p className="text-ink pt-2">C. <span className="text-ink-dim">Igiene generale</span></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Salva le 24 parole <b>su carta</b>, mai screenshot, cloud o foto.
            Sono l'<b>unica</b> chiave per recuperare la tua identità se perdi
            PIN e device.</li>
          <li>Cambia il PIN ogni tanto: menu → ⚙ Impostazioni → "Cambia PIN".</li>
          <li>Usa un PIN diverso da quello che usi altrove.</li>
          <li>Temi che il device sia compromesso? Menu → ⚙ Impostazioni →
            "🗑 Svuota questo dispositivo", poi rientra da un altro device con
            le 24 parole. Il vecchio perde l'accesso.</li>
          <li>Tieni l'app aggiornata: menu → ⚙ Impostazioni → "🔄 Verifica
            aggiornamenti". Le correzioni di sicurezza arrivano lì.</li>
        </ul>

        <p className="text-ink pt-2">D. <span className="text-ink-dim">Per casi estremi</span></p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Apri Nullum in finestra incognito o Tor Browser: nessun account
            resta sul device, l'identità dura solo la sessione.</li>
          <li>Non mettere il tuo sigil su social o forum. Usa solo gli inviti.</li>
          <li>Disattiva le notifiche push: nessun tuo token sul server.</li>
          <li>Passa in <b>modalità Riservata</b> (menu → Premium): il tuo
            sigil sparisce dalla ricerca.</li>
        </ul>

        <div className="mt-3 border border-red-500/40 p-3 rounded space-y-2">
          <p className="text-red-400">☢ Nuke 5-PIN {nuke ? '— ATTIVO' : '— spento'}</p>
          <p className="text-[13px]">
            Se attivo, 5 PIN sbagliati di fila su questo device cancellano
            TUTTO (chiavi, sigil, backup): l'app torna come appena installata.
            Difesa contro chi ti forza il telefono. Recuperi solo con le 24 parole.
          </p>
          <button
            onClick={toggleNuke}
            className={`border px-3 py-2 text-[14px] ${nuke ? 'border-red-500 text-red-400' : 'border-ink-dim text-ink hover:border-ink'}`}
          >
            {nuke ? '☢ Disattiva Nuke 5-PIN' : '☢ Attiva Nuke 5-PIN'}
          </button>
        </div>
      </Section>

      {/* ============================================================ */}
      <Section id="tech" title="💎 Perché Nullum è diversa">
        <p>
          Le altre app proteggono <b className="text-ink">quello che scrivi</b>.
          Nullum protegge anche <b className="text-ink">chi sei</b>: niente numero,
          niente email, niente nome. La tua identità è un nome che scegli tu
          (@sigil) e 24 parole che restano solo tue.
        </p>
        <p>
          Spesso il <b className="text-ink">solo fatto</b> che una certa persona
          ti abbia scritto, e quando, dice più del messaggio. Per un avvocato è
          il nome del cliente; per un medico è già mezza diagnosi. Nullum nasconde
          anche questo.
        </p>

        <p className="text-ink pt-1">I tuoi vantaggi, tutti insieme e già attivi</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Nessun dato personale.</b> Niente numero, email o nome: la tua
            identità non è legata a te nel mondo reale.</li>
          <li><b>Più identità separate.</b> Puoi tenere più @sigil sullo stesso
            telefono — lavoro, privato, usa-e-getta — e nessuno può collegarli tra
            loro.</li>
          <li><b>Il server non sa nemmeno chi ti scrive.</b> Nel canale Anonimo il
            messaggio arriva senza mittente, con protezioni anti-inganno e anti-spam.</li>
          <li><b>Solo su invito.</b> Non c'è un elenco di utenti: nessuno può
            cercarti o scoprire che ci sei.</li>
          <li><b>I messaggi spariscono da soli.</b> Scegli tu quanto durano; gli
            allegati si vedono una volta sola. Di serie.</li>
          <li><b>Tutto in Europa.</b> Nullum è italiana (SKEMATIKA SRL), i dati
            stanno su server europei sotto GDPR e non escono mai dall'UE.</li>
          <li><b>Veloce e affidabile.</b> Nullum gira su infrastruttura europea
            solida: i messaggi arrivano subito e le notifiche funzionano. Il server
            è fatto per non sapere nulla di te.</li>
        </ul>
        <Box className="text-[13px] mt-1">
          <b className="text-ink">La forza è la somma:</b> identità senza dati,
          messaggi effimeri, mittente nascosto e solo-su-invito, tutto dentro
          l'Unione Europea.
        </Box>

        <p className="text-ink pt-2">Nullum a confronto</p>
        <p>Le migliori app per la privacy si equivalgono su molti punti. La forza di
          Nullum è la <b className="text-ink">somma</b>: la stessa protezione delle
          migliori, ma <b>tutta in UE</b> e con i messaggi <b>effimeri di serie</b>.
          <span className="text-ink-dim"> (scorri la tabella →)</span></p>
        <div className="overflow-x-auto -mx-1 mt-1">
          <table className="text-[12px] border-collapse whitespace-nowrap">
            <thead>
              <tr className="text-ink-dim">
                <th className="text-left font-normal p-1.5 border-b border-ink-dim/40"> </th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40">WhatsApp</th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40">Telegram</th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40">Signal</th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40">Session</th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40">SimpleX</th>
                <th className="font-normal p-1.5 border-b border-ink-dim/40 text-ink text-glow">Nullum</th>
              </tr>
            </thead>
            <tbody className="text-center">
              <tr><td className="p-1.5 text-ink-dim text-left">Senza numero o email</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">Sì</td><td className="p-1.5">Sì</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Il server non sa chi parla con chi</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">~</td><td className="p-1.5">Sì</td><td className="p-1.5">Sì</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Nessun modo per cercarti</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">Sì</td><td className="p-1.5">Sì</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Più identità scollegabili</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">Sì</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Messaggi effimeri di serie</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Ricevi anche da anonimi</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">IP nascosto integrato</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">No</td><td className="p-1.5">Sì</td><td className="p-1.5">~</td><td className="p-1.5 text-ink">Sì</td></tr>
              <tr><td className="p-1.5 text-ink-dim text-left">Dati / sede</td><td className="p-1.5">USA</td><td className="p-1.5">fuori UE</td><td className="p-1.5">USA</td><td className="p-1.5">Svizzera</td><td className="p-1.5">UK</td><td className="p-1.5 text-ink">UE 🇪🇺</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-[12px] text-ink-dim">~ = in parte. <b className="text-ink">Session</b> e <b className="text-ink">SimpleX</b> sono le più vicine: quello che nessuna ha è <b>tutto in UE</b> + effimero di serie + casella anonima aperta.</p>

        <p className="text-ink pt-3">🎭 L'identità anonima</p>
        <p>Per i momenti che contano, crea un @sigil <b>nuovo e dedicato</b>.
          Nessuno può collegarlo a te né alle tue altre identità: è il tuo
          "usa e getta".</p>
        <p className="text-[13px] text-ink-dim">Regola d'oro: usalo <b className="text-ink">sempre con l'IP nascosto</b>
          (vedi sotto), fin dalla prima volta. Così non è mai legato a dove sei.</p>

        <p className="text-ink pt-3">📭 L'inbox aperta — a cosa serve</p>
        <p>L'inbox aperta <b className="text-ink">non ti rende più sicuro</b>:
          rinunci al filtro su chi ti scrive. Serve a <b>una cosa sola</b>: farti
          raggiungere da <b>sconosciuti che vogliono restare anonimi</b>.</p>
        <p>Di solito sei tu ad accettare chi ti scrive, e questo crea una lista di
          richieste legata a te. Con l'inbox aperta la chat parte subito, <b>senza
          quella lista</b>. Con il mittente nascosto, chi ti scrive non risulta a
          nessuno, nemmeno a te. È la <b className="text-ink">cassetta delle
          segnalazioni</b> del giornalista: protegge l'anonimato di <b>chi ti
          contatta</b>, non il tuo. Attivala <b>solo</b> se ti serve questo.</p>

        <p className="text-ink pt-3">🧅 IP nascosto con un tocco (Tor)</p>
        <p>Il resto nasconde chi sei; Tor nasconde anche <b>da dove</b> ti colleghi
          (il tuo indirizzo IP). Nell'app Android lo attivi
          <b className="text-ink"> con un tocco, dentro Nullum</b>, senza altre app.
          Una <b>spia in alto</b> (🧅) ti dice sempre se sei su Tor.</p>
        <Box variant="success" className="text-[13px]">
          Gli strati insieme: il <b className="text-ink">contenuto</b> è cifrato,
          <b className="text-ink"> chi sei</b> non risulta, <b className="text-ink">chi
          scrive</b> è nascosto e, con Tor, anche <b className="text-ink">da dove</b>.
        </Box>

        <Section id="tech-deep" title="🔩 Sotto il cofano — per chi vuole i dettagli">
          <p>Se questi nomi non ti dicono niente, va bene: la protezione è già
            attiva. Per i più tecnici:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>End-to-end</b> con <b>libsodium</b>: XChaCha20-Poly1305 per il
              contenuto, X25519 per le chiavi, BLAKE2b per gli hash.</li>
            <li><b>Forward secrecy</b> con <b>doppio ratchet</b>: una chiave nuova a
              ogni messaggio, così una chiave compromessa non espone i messaggi
              passati.</li>
            <li><b>Ibrido post-quantico</b> (X25519 + <b>ML-KEM-768</b>, standard NIST
              FIPS 203): protegge dal "registra oggi, decifra domani".</li>
            <li>Le 24 parole seguono lo standard <b>BIP-39</b>; le chiavi locali sono
              protette con <b>Argon2id</b> + limite dei tentativi lato server.</li>
            <li>Canale Anonimo: busta senza mittente, firma anti-spoofing e
              <b> proof-of-work</b> anti-flood, il tutto effimero.</li>
          </ul>
        </Section>
      </Section>

      {/* ============================================================ */}
      <Section id="gdpr" title="📜 Privacy & GDPR">
        <p className="text-ink">Titolare del trattamento</p>
        <Box className="text-[13px]">
          <p><b>SKEMATIKA SRL</b></p>
          <p>Sede legale: Via Pier Luigi Nervi 190, 04100 Latina (LT), Italia</p>
          <p>C.F. / P.IVA: 03198400594</p>
          <p>REA: LT - 311897</p>
          <p>PEC: skematika@pec.it</p>
          <p>Amministratore Unico: Fabrizio Nardoni</p>
          <p>Contatto utenti: info@nullum.app</p>
        </Box>

        <p className="text-ink pt-2">Dati che raccogliamo</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Il tuo pseudonimo (@sigil), scelto da te.</li>
          <li>Le tue chiavi pubbliche di cifratura (servono per riceverti).</li>
          <li>Il tuo <b>backup</b>: un pacchetto cifrato con le tue 24 parole.
            Per il server è illeggibile, la chiave per aprirlo non lascia mai
            il tuo device.</li>
          <li>Il tuo token push FCM (solo se attivi le notifiche).</li>
          <li>Messaggi cifrati (ciphertext + nonce) e timestamp, fino alla
            scadenza TTL.</li>
          <li>Allegati cifrati (foto/video/audio/file) fino al one-view.</li>
        </ul>

        <p className="text-ink pt-2">Dati che NON raccogliamo (mai)</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Il contenuto dei messaggi: è cifrato, nemmeno noi possiamo
            leggerlo.</li>
          <li>Nome, cognome, email, telefono, indirizzo.</li>
          <li>Foto profilo, rubrica, contatti.</li>
          <li>La tua posizione. Se la condividi tu con un contatto, è contenuto
            cifrato che non leggiamo e che sparisce da solo.</li>
          <li>Cookie di tracciamento o analytics.</li>
        </ul>

        <p className="text-ink pt-2">Base giuridica</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Esecuzione del servizio richiesto (art. 6.1.b GDPR).</li>
          <li>Consenso esplicito (art. 6.1.a GDPR) per le notifiche push:
            disattivabile in qualsiasi momento da menu → ⚙ Impostazioni.</li>
        </ul>

        <p className="text-ink pt-2">Conservazione</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Messaggi: fino allo scadere del TTL scelto dal mittente.</li>
          <li>Allegati: cancellati dopo il one-view; comunque entro il TTL.</li>
          <li>Chat senza attività: ripulite server-side dopo 30 minuti.</li>
          <li>Backup remoto cifrato: fino alla tua cancellazione.</li>
          <li>Token push: fino a quando le notifiche restano attive.</li>
        </ul>

        <p className="text-ink pt-2">Trasferimenti</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Dati ospitati su <b>Google Cloud / Firebase</b> nelle region
            europe-west1 (Belgio) ed europe-west3 (Germania). Nessun
            trasferimento extra-UE.</li>
        </ul>

        <p className="text-ink pt-2">I tuoi diritti</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Accesso: i messaggi nelle tue chat sono già a tua disposizione
            decifrati sul tuo device.</li>
          <li>Rettifica: cambia sigil ri-claimando un nuovo @nome.</li>
          <li>Cancellazione: "🗑 Svuota questo dispositivo" rimuove tutto in
            locale. Per cancellare anche il backup sul server, scrivi a
            info@nullum.app.</li>
          <li>Portabilità: il contenuto è cifrato, possiamo esportarti solo i
            metadati che il server vede (sigil, chiavi pubbliche, orari).</li>
          <li>Opposizione e limitazione: contattaci a info@nullum.app.</li>
          <li>Reclamo: <b>Garante per la protezione dei dati personali</b>
            (garanteprivacy.it).</li>
        </ul>

        <p className="text-[12px] pt-2">
          Servizio non destinato a minori di 16 anni. Per le richieste
          formali scrivi alla PEC: skematika@pec.it.
        </p>
      </Section>

      {/* ============================================================ */}
      <Section id="manual" title="❓ Cosa scegliere — guida rapida">
        <p>Domandati: <b>cosa mi serve adesso?</b></p>

        <p className="text-ink pt-1">«Voglio scrivere a uno sconosciuto senza farmi tracciare»</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Invito da 1 ora o meno, QR code, senza etichetta.</li>
          <li>Durata messaggi: 10s. "Rileggi i miei messaggi": OFF.</li>
          <li>Per verificare la persona, manda un audio o video live
            (🎙 / 🎥 dal menu allegati).</li>
        </ul>

        <p className="text-ink pt-1">«Mi serve solo una chiacchierata di 2 minuti»</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Invito da "1 minuto", monouso. Apri la chat, parla, chiudi.</li>
          <li>Durata messaggi "zero": spariscono subito dopo la lettura.</li>
        </ul>

        <p className="text-ink pt-1">«Voglio una chat duratura con un amico fidato»</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Invito da 24h o più. La prima volta che vi vedete, leggete a voce
            il codice di sicurezza.</li>
          <li>Durata: 1h, o "5m" se preferisci comunque l'effimero.</li>
          <li>"Rileggi i miei messaggi": ON se ti serve la storia, OFF per zero
            tracce.</li>
        </ul>

        <p className="text-ink pt-1">«Devo mandare un documento, una foto, un vocale»</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Tocca 📎 → galleria, video, audio o un file qualsiasi. Fino a
            24 MB cifrati.</li>
          <li>Si vede una volta sola: dopo che l'altro lo apre, sparisce dal
            server.</li>
          <li>Cambiato idea prima che lo apra? "✕ annulla" e sparisce.</li>
        </ul>

        <p className="text-ink pt-1">«Sono in un paese dove la sorveglianza è un rischio»</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Apri Nullum sempre da finestra incognito.</li>
          <li>Tieni le 24 parole offline, mai online.</li>
          <li>Allegati usa-e-getta, durata "zero", "Rileggi i miei messaggi" OFF.</li>
          <li>Verifica il codice di sicurezza di ogni contatto prima di ogni
            scambio sensibile.</li>
          <li>Disattiva le notifiche push: nessun tuo token sul server.</li>
        </ul>
      </Section>

      <Footer>
        <Btn variant="ghost" onClick={() => navigate('/inbox')}>◀ Inbox</Btn>
      </Footer>
    </Frame>
  );
}
